import { createContext, useContext, useMemo } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { type Brand, type User } from '@/types/user';
import { domainMeta } from '@/utils/domain-meta';

type AuthContextType = {
  user?: User | null;
  account: Brand | null;
  brandLogoUrl: string | null;
  brandSquareLogoUrl: string | null;
  isAuthenticated: boolean;
  hasSourceAccess: boolean;
  canPostToJobBoards: boolean;
  isLoading: boolean;
  isError: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export enum PERMISSION_MAPPING {
  ADMIN = 'admin',
  POST_TO_JOB_BOARDS = 'post_to_job_boards',
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data: user, isLoading, isError } = useAuth();

  const account = useMemo(() => {
    if (!user) return null;

    return user.brands.filter(brand => brand.isAccount)[0];
  }, [user]);

  const [brandLogoUrl, brandSquareLogoUrl] = useMemo(() => {
    if (!user) return [null, null];
    const brand = user.brands.find(b => b.default);
    if (!brand) return [null, null];
    const getUrl = (path?: string) => {
      if (!path) return null;
      return new URL(path, domainMeta.monolithOrigin).href;
    };
    return [getUrl(brand.logo_url), getUrl(brand.square_logo_url)];
  }, [user, domainMeta.monolithOrigin]);

  return (
    <AuthContext.Provider
      value={{
        user,
        account,
        brandLogoUrl,
        brandSquareLogoUrl,
        isAuthenticated: !!user,
        hasSourceAccess:
          user?.feature_flags?.['sourcing-dashboard-user-access'] === true,
        canPostToJobBoards: !!(
          user?.perms.includes(PERMISSION_MAPPING.ADMIN) ||
          user?.perms.includes(PERMISSION_MAPPING.POST_TO_JOB_BOARDS)
        ),
        isLoading,
        isError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context)
    throw new Error('useAuthContext must be used within <AuthProvider>');

  return context;
};
